import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminFetch } from '../hooks/useAdminFetch'

interface PlanLimits {
  plan: string
  max_uploads_month: number
  max_ai_calls_month: number
  max_connectors: number
  max_seats: number
  updated_at: string
}

const PLAN_LABELS: Record<string, string> = {
  trial: 'Trial',
  starter: 'Starter',
  growth: 'Growth',
  enterprise: 'Enterprise',
}

const PLAN_COLORS: Record<string, string> = {
  trial: 'var(--km-text-muted)',
  starter: 'var(--km-primary)',
  growth: 'var(--km-success)',
  enterprise: '#a78bfa',
}

const COLUMNS = [
  { key: 'max_uploads_month', label: 'Upload/mese' },
  { key: 'max_ai_calls_month', label: 'AI call/mese' },
  { key: 'max_connectors', label: 'Connettori' },
  { key: 'max_seats', label: 'Seat' },
] as const

type LimitKey = (typeof COLUMNS)[number]['key']

function formatLimit(val: number, plan: string): string {
  if (val === 0 && plan === 'enterprise') return '∞'
  return val.toString()
}

function CellInput({
  value,
  plan,
  onChange,
  disabled,
}: {
  value: number
  plan: string
  onChange: (v: number) => void
  disabled: boolean
}) {
  if (plan === 'enterprise') {
    return (
      <span className="text-sm font-medium" style={{ color: '#a78bfa' }}>
        ∞
      </span>
    )
  }
  return (
    <input
      type="number"
      min={1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Math.max(1, parseInt(e.target.value) || 1))}
      className="w-24 text-sm text-center rounded px-2 py-1 border bg-transparent outline-none focus:ring-1"
      style={{
        borderColor: 'var(--km-border)',
        color: 'var(--km-text-primary)',
        background: 'var(--km-surface-2)',
      }}
    />
  )
}

export function AdminPlanLimits() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<Record<string, Record<LimitKey, number>>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading, isError, error: queryError } = useQuery<PlanLimits[]>({
    queryKey: ['admin', 'plan-limits'],
    queryFn: () => adminFetch('/api/v1/admin/plan-limits'),
  })

  const mutation = useMutation({
    mutationFn: ({ plan, body }: { plan: string; body: Record<LimitKey, number> }) =>
      adminFetch<PlanLimits>(`/api/v1/admin/plan-limits/${plan}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (updated) => {
      queryClient.setQueryData<PlanLimits[]>(['admin', 'plan-limits'], (old) =>
        old ? old.map((r) => (r.plan === updated.plan ? updated : r)) : [updated]
      )
      setSaved(updated.plan)
      setTimeout(() => setSaved(null), 2000)
    },
    onError: (err: Error) => {
      setError(err.message)
      setTimeout(() => setError(null), 4000)
    },
    onSettled: () => setSaving(null),
  })

  function startEdit(row: PlanLimits) {
    setEditing((prev) => ({
      ...prev,
      [row.plan]: {
        max_uploads_month: row.max_uploads_month,
        max_ai_calls_month: row.max_ai_calls_month,
        max_connectors: row.max_connectors,
        max_seats: row.max_seats,
      },
    }))
  }

  function cancelEdit(plan: string) {
    setEditing((prev) => {
      const next = { ...prev }
      delete next[plan]
      return next
    })
  }

  function handleChange(plan: string, key: LimitKey, val: number) {
    setEditing((prev) => ({
      ...prev,
      [plan]: { ...prev[plan], [key]: val },
    }))
  }

  function handleSave(plan: string) {
    const body = editing[plan]
    if (!body) return
    setSaving(plan)
    setError(null)
    mutation.mutate({ plan, body })
    cancelEdit(plan)
  }

  if (isLoading) {
    return (
      <div className="text-sm" style={{ color: 'var(--km-text-muted)' }}>
        Caricamento limiti piano...
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
          Limiti per piano
        </h2>
        <div
          className="text-sm px-4 py-3 rounded-lg"
          style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--km-error)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          Impossibile caricare i limiti piano:{' '}
          {queryError instanceof Error ? queryError.message : 'Errore sconosciuto'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold" style={{ color: 'var(--km-text-primary)' }}>
            Limiti per piano
          </h2>
          <p className="text-xs mt-1" style={{ color: 'var(--km-text-muted)' }}>
            Modifica i valori soglia operativi per ogni piano. Il valore 0 è riservato a Enterprise (illimitato).
            Le modifiche hanno effetto immediato senza deploy.
          </p>
        </div>
      </div>

      {error && (
        <div
          className="text-sm px-4 py-2 rounded-lg"
          style={{ background: 'rgba(239,68,68,0.1)', color: 'var(--km-error)', border: '1px solid rgba(239,68,68,0.2)' }}
        >
          {error}
        </div>
      )}

      <div
        className="rounded-xl border overflow-hidden"
        style={{ borderColor: 'var(--km-border)', background: 'var(--km-surface)' }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--km-border)', background: 'var(--km-surface-2)' }}>
              <th className="text-left px-5 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--km-text-muted)' }}>
                Piano
              </th>
              {COLUMNS.map((col) => (
                <th key={col.key} className="text-center px-4 py-3 font-semibold text-xs uppercase tracking-wider" style={{ color: 'var(--km-text-muted)' }}>
                  {col.label}
                </th>
              ))}
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {(data ?? []).map((row, idx) => {
              const isEditing = !!editing[row.plan]
              const isSaving = saving === row.plan
              const isSaved = saved === row.plan
              const vals = editing[row.plan] ?? row

              return (
                <tr
                  key={row.plan}
                  style={{
                    borderBottom: idx < (data?.length ?? 0) - 1 ? '1px solid var(--km-border)' : undefined,
                    background: isEditing ? 'rgba(99,102,241,0.04)' : undefined,
                  }}
                >
                  {/* Piano */}
                  <td className="px-5 py-4">
                    <span className="font-semibold text-sm" style={{ color: PLAN_COLORS[row.plan] }}>
                      {PLAN_LABELS[row.plan] ?? row.plan}
                    </span>
                    {row.plan === 'enterprise' && (
                      <span className="ml-2 text-[10px] opacity-60" style={{ color: 'var(--km-text-muted)' }}>
                        illimitato
                      </span>
                    )}
                  </td>

                  {/* Valori */}
                  {COLUMNS.map((col) => (
                    <td key={col.key} className="px-4 py-4 text-center">
                      {isEditing ? (
                        <CellInput
                          value={(vals as Record<LimitKey, number>)[col.key]}
                          plan={row.plan}
                          onChange={(v) => handleChange(row.plan, col.key, v)}
                          disabled={isSaving}
                        />
                      ) : (
                        <span style={{ color: 'var(--km-text-primary)' }}>
                          {formatLimit(row[col.key], row.plan)}
                        </span>
                      )}
                    </td>
                  ))}

                  {/* Azioni */}
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    {row.plan === 'enterprise' ? (
                      <span className="text-xs" style={{ color: 'var(--km-text-muted)' }}>—</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => cancelEdit(row.plan)}
                          className="text-xs px-3 py-1 rounded border transition-colors hover:bg-white/5"
                          style={{ borderColor: 'var(--km-border)', color: 'var(--km-text-muted)' }}
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => handleSave(row.plan)}
                          disabled={isSaving}
                          className="text-xs px-3 py-1 rounded font-medium transition-colors"
                          style={{ background: 'var(--km-primary)', color: '#fff', opacity: isSaving ? 0.6 : 1 }}
                        >
                          {isSaving ? 'Salvo...' : 'Salva'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEdit(row)}
                        className="text-xs px-3 py-1 rounded border transition-colors hover:bg-white/5"
                        style={{ borderColor: 'var(--km-border)', color: isSaved ? 'var(--km-success)' : 'var(--km-text-secondary)' }}
                      >
                        {isSaved ? 'Salvato ✓' : 'Modifica'}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs" style={{ color: 'var(--km-text-muted)' }}>
        Aggiornamento ultimo: {data?.[0]?.updated_at ? new Date(data[0].updated_at).toLocaleString('it-IT') : '—'}
      </p>
    </div>
  )
}
